import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import Description from '@/components/Description/Description'
import FormInput from '@/components/Common/FormInput'
import PasswordInput from '@/components/Common/PasswordInput'
import StyledBgContainer from '@/components/StyledBgContainer/StyledBgContainer'
import StyledButton from '@/components/Button/Button'
import TopNav from '@/components/TopNav/TopNav'
import useSignInAdmin from '../services/hooks/usePostLogin'
import useLocalStorage from '@/hooks/useLocalStorage'
import useValidation from '@/hooks/useValidation'
import Path from '@/router/localRoutes'
import EmailIcon from '@/assets/Message 36.svg'
import './Signin.css'
import { useWebSocket } from '@/context/WebSocketContext'

const Signin = () => {
  const { resetSocket } = useWebSocket()
  const inputValidationRules = {
    email: [
      { required: true, message: 'Please enter your email address.' },
      {
        pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: 'Please enter a valid email address.',
      },
    ],
    password: [
      { required: true, message: 'Please enter your password.' },
      {
        pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
        message:
          'Password must contain at least 8 characters, including uppercase, lowercase letters, and a number.',
      },
    ],
  }

  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [roleValue] = useLocalStorage('role')
  const [, setToken] = useLocalStorage('token')
  const { errors, validateInputs } = useValidation(
    {
      email,
      password,
    },
    inputValidationRules,
  )

  const navigate = useNavigate()

  const { mutate: loginAdmin, isPending } = useSignInAdmin()

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const onChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
  }

  const handleSignIn = () => {
    const role = roleValue
    if (!role) {
      navigate(Path.Root)
    }
    if (!validateInputs()) {
      return
    }
    const requestData = {
      email,
      password,
      role,
    }

    loginAdmin(requestData, {
      onSuccess: (response) => {
        setToken(response.data.token)
        navigate(Path.AdminDashboard)
        resetSocket()
      },
      onError: (error) => {
        setErrorMsg(error.message || 'Something went wrong. Please try again.')
      },
    })
  }

  return (
    <StyledBgContainer>
      <div className="sign-in-container">
        <TopNav />
        <h1 className="heading  mb-3 font-bold">Sign In</h1>
        <Description>
          Please enter your credentials to access your account
        </Description>
        <div className="login-form w-[556px] gap-6">
          <FormInput
            type="email"
            onChange={onChangeEmail}
            value={email}
            label="Email Address"
            startIconComponent={
              <img src={EmailIcon} alt="Email icon" className="text-white" />
            }
            error={
              !!errors.email ||
              errorMsg === 'Invalid email format' ||
              errorMsg.includes('User')
            }
            helperText={errors.email || errorMsg}
          />
          <PasswordInput
            onChange={onChangePassword}
            value={password}
            label="Enter Password"
            className="password-field"
            error={!!errors.password || errorMsg === 'Invalid password'}
            helperText={errors.password || errorMsg}
          />
          <div className="error-container">
            <Link to={Path.ForgotPassword} className="forgot-password">
              Forgot Password ?
            </Link>
          </div>
        </div>
        <div className="absolute bottom-28 left-0 right-0 mb-8 flex flex-col items-center">
          {isPending ? (
            <StyledButton className=" w-[100px] !h-[45px]">
              <CircularProgress className="!text-[#181a21] !text-2xl !text-[14px]" />
            </StyledButton>
          ) : (
            <StyledButton
              className="!shadow-2xl !font-sans w-[100px] !h-[45px] !text-[14px]"
              onClick={handleSignIn}
              disabled={!(email && password)}
            >
              Sign In
            </StyledButton>
          )}
        </div>
      </div>
    </StyledBgContainer>
  )
}

export default Signin
